import { useCallback, useMemo, useReducer } from 'react';

type PaginationState = {
  sort: string;
  includeCursor?: boolean;
  cursorBefore: string | null;
  cursorAfter: string | null;
};

export type FiltersState = {
  status?: string;
  accountNumber?: string;
  reason_code_id?: number;
};

export type LocalState = PaginationState & { filters: FiltersState };

export type Action =
  | { type: 'reset' }
  | { type: 'setSort'; value: string }
  | { type: 'updateCursorBefore'; value: string | null }
  | { type: 'setCursorAfter'; value: string | null }
  | { type: 'updateFilters'; value: LocalState['filters'] }
  | { type: 'updateAccountNumber'; value: string | undefined }
  | {
      type: 'updateStatusAndReasonCode';
      value: { status?: string; reasonCode?: number };
    };

function reducer(state: LocalState, action: Action): LocalState {
  switch (action.type) {
    case 'setSort':
      return {
        ...state,
        sort: action.value,
        includeCursor: undefined,
        cursorBefore: null,
        cursorAfter: null
      };
    case 'updateCursorBefore':
      return {
        ...state,
        cursorBefore: action.value,
        cursorAfter: null,
        includeCursor: undefined
      };
    case 'setCursorAfter':
      return {
        ...state,
        cursorAfter: action.value,
        cursorBefore: null,
        includeCursor: undefined
      };
    case 'updateFilters':
      return { ...state, filters: action.value };
    case 'updateAccountNumber':
      return {
        ...state,
        filters: { ...state.filters, accountNumber: action.value }
      };
    case 'updateStatusAndReasonCode':
      return {
        ...state,
        filters: {
          ...state.filters,
          status: action.value.status,
          reason_code_id: action.value.reasonCode
        }
      };
    case 'reset':
      return {
        filters: {
          status: 'All',
          accountNumber: 'All',
          reason_code_id: undefined
        },
        sort: '-date',
        cursorAfter: null,
        cursorBefore: null,
        includeCursor: undefined
      };
  }
}

const reset = (): Action => ({ type: 'reset' });
const setSort = (value: string): Action => ({ type: 'setSort', value });
const updateCursorBefore = (value: string | null): Action => ({
  type: 'updateCursorBefore',
  value
});
const setCursorAfter = (value: string | null): Action => ({
  type: 'setCursorAfter',
  value
});

const updateFilters = (value: LocalState['filters']): Action => ({
  type: 'updateFilters',
  value
});

const updateAccountNumber = (value: string | undefined): Action => ({
  type: 'updateAccountNumber',
  value
});

const updateStatusAndReasonCode = (value: {
  status?: string;
  reasonCode?: number;
}): Action => ({
  type: 'updateStatusAndReasonCode',
  value
});

const initialState: LocalState = {
  filters: {
    status: 'All',
    accountNumber: 'All',
    reason_code_id: undefined
  },
  sort: '-date',
  includeCursor: undefined,
  cursorBefore: null,
  cursorAfter: null
};

export function useLocalState() {
  const searchParams = new URL(document.location.toString()).searchParams;

  // Merge values from query params with initialState
  const mergedInitialState: LocalState = {
    filters: {
      status: searchParams.get('status') || initialState.filters.status,
      accountNumber:
        searchParams.get('accountNumber') || initialState.filters.accountNumber,
      reason_code_id: initialState.filters.reason_code_id
    },
    sort: searchParams.get('sort') || initialState.sort,
    includeCursor: initialState.includeCursor,
    cursorBefore: searchParams.get('cursorBefore') || initialState.cursorBefore,
    cursorAfter: searchParams.get('cursorAfter') || initialState.cursorAfter
  };

  const [state, dispatch] = useReducer(reducer, mergedInitialState);

  const updateCursorAfter = useCallback(
    (value: string | null) => dispatch(setCursorAfter(value)),
    [dispatch]
  );

  const queryParams = useMemo(() => {
    return {
      accountNumber: state.filters.accountNumber,
      status: state.filters.status,
      reason_code_id: state.filters.reason_code_id,
      cursorAfter: state.cursorAfter,
      cursorBefore: state.cursorBefore,
      sort: state.sort
    };
  }, [state]);

  return {
    state,
    queryParams,
    dispatch,
    reset,
    setSort,
    updateCursorBefore,
    updateCursorAfter,
    updateFilters,
    updateAccountNumber,
    updateStatusAndReasonCode
  };
}
